import * as React from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ThemeProvider, alpha, createTheme, type Theme } from '@mui/material/styles';

// Apparence de l'application : 'auto' suit le reglage du systeme
// (prefers-color-scheme), 'clair' et 'sombre' l'imposent. Le choix est
// propre a l'appareil (memorise dans le navigateur), comme sur la plupart des
// sites : un telephone peut rester en automatique et un ordinateur en clair.
export type ChoixTheme = 'auto' | 'clair' | 'sombre';

const CLE_STOCKAGE = 'apparence';

function lireChoix(): ChoixTheme {
  try {
    const v = localStorage.getItem(CLE_STOCKAGE);
    if (v === 'clair' || v === 'sombre' || v === 'auto') return v;
  } catch {
    // Stockage indisponible (navigation privee) : automatique.
  }
  return 'auto';
}

function memoriserChoix(choix: ChoixTheme) {
  try {
    localStorage.setItem(CLE_STOCKAGE, choix);
  } catch {
    // Choix conserve pour la session seulement.
  }
}

type ContexteTheme = {
  choix: ChoixTheme;
  // Mode effectivement applique (le systeme tranche en automatique).
  mode: 'light' | 'dark';
  choisir: (choix: ChoixTheme) => void;
};

const Contexte = React.createContext<ContexteTheme>({ choix: 'auto', mode: 'light', choisir: () => {} });

export function useChoixTheme(): ContexteTheme {
  return React.useContext(Contexte);
}

// Variables CSS pour les styles en ligne (style={{ ... }}) qui n'ont pas
// acces au theme MUI : pages d'accueil notamment.
function variablesCss(theme: Theme) {
  const sombre = theme.palette.mode === 'dark';
  return {
    ':root': {
      '--fond-tuile': sombre ? theme.palette.background.paper : '#ffffff',
      '--fond-tuile-doux': sombre ? alpha('#ffffff', 0.05) : '#f7f7fb',
      '--fond-neutre': sombre ? alpha('#ffffff', 0.04) : '#fafafa',
      '--fond-note': sombre ? alpha('#f9a825', 0.12) : '#fffde7',
      '--bord-tuile': sombre ? alpha('#ffffff', 0.12) : '#e0e0f0',
      '--texte': sombre ? theme.palette.text.primary : '#0f172a',
      '--texte-secondaire': sombre ? theme.palette.text.secondary : '#6b7280',
      '--succes': sombre ? theme.palette.success.light : '#2e7d32',
      '--erreur': sombre ? theme.palette.error.light : '#c62828',
    },
    // Liens en ligne lisibles sur fond sombre.
    ...(sombre ? { 'a:not([class])': { color: theme.palette.primary.light } } : {}),
  };
}

// Fournit le theme (clair ou sombre) a toute l'application, y compris
// l'ecran de connexion.
export default function ThemeApplication({ children }: { children: React.ReactNode }) {
  const systemeSombre = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true });
  const [choix, setChoix] = React.useState<ChoixTheme>(lireChoix);

  const choisir = React.useCallback((c: ChoixTheme) => {
    memoriserChoix(c);
    setChoix(c);
  }, []);

  // Choix change dans un autre onglet : applique ici aussi.
  React.useEffect(() => {
    const surStockage = (e: StorageEvent) => {
      if (e.key === CLE_STOCKAGE) setChoix(lireChoix());
    };
    window.addEventListener('storage', surStockage);
    return () => window.removeEventListener('storage', surStockage);
  }, []);

  const mode: 'light' | 'dark' = choix === 'auto' ? (systemeSombre ? 'dark' : 'light') : choix === 'sombre' ? 'dark' : 'light';
  const theme = React.useMemo(() => createTheme({ palette: { mode } }), [mode]);
  const valeur = React.useMemo(() => ({ choix, mode, choisir }), [choix, mode, choisir]);

  return (
    <Contexte.Provider value={valeur}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        <GlobalStyles styles={variablesCss(theme)} />
        {children}
      </ThemeProvider>
    </Contexte.Provider>
  );
}
